import { Injectable, Injector } from '@angular/core';
import { Overlay, OverlayConfig, OverlayRef, ConnectionPositionPair, PositionStrategy, FlexibleConnectedPositionStrategyOrigin } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { SelectedItemsVisualizerOverlayComponent } from './selected-items-visualizer-overlay.component';
import { SelectedItemsVisualizerOverlayRef } from './selected-items-visualizer-overlay-ref';
import { Observable } from 'rxjs';
import { ItemNode } from '../models/item';
import { ItemListInjectionToken } from './item-list-injection-token';
import { DIALOG_REF_TOKEN, SELECTED_ITEMS_STREAM, ITEM_LIST } from './selected-items-overlay.token';

@Injectable({
    providedIn: 'root'
})
export class SelectedItemsVisualizerOverlayService {

    public isOverlayOpened = false;

    constructor(private overlay: Overlay, private injector: Injector) { }

    open(origin: FlexibleConnectedPositionStrategyOrigin, selectedItemsObservable: Observable<ItemNode[]>, itemList: ItemNode[]): SelectedItemsVisualizerOverlayRef {
        // Returns an OverlayRef which is a PortalHost
        const overlayRef = this.createOverlay(origin);
        const dialogRef = new SelectedItemsVisualizerOverlayRef(overlayRef);
        this.isOverlayOpened = !overlayRef.hasAttached();
        // Attach ComponentPortal to PortalHost
        this.attachDialogContainer(overlayRef, dialogRef, selectedItemsObservable, itemList);

        // Subscribe to a stream that emits when the backdrop was clicked
        overlayRef.backdropClick().subscribe(_ => {
            dialogRef.close();
        });
        return dialogRef;
    }

    private getOverlayConfig(origin: FlexibleConnectedPositionStrategyOrigin): OverlayConfig {
        return new OverlayConfig({
            hasBackdrop: false,
            panelClass: 'selectorOverlayPanel',
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
            positionStrategy: this.getOverlayPosition(origin),
            width: '375px',
            height: '300px'
        });
    }

    private getOverlayPosition(origin: FlexibleConnectedPositionStrategyOrigin): PositionStrategy {
        return this.overlay.position().flexibleConnectedTo(origin).withPositions(this.getPositions()).withPush(false);
    }

    private getPositions(): ConnectionPositionPair[] {
        return [
            { // destra
                offsetX: 0,
                offsetY: 0,
                originX: 'end', // start, end, or center
                originY: 'top', // top, bottom or center
                overlayX: 'start', //'start', 'end', or 'center'
                overlayY: 'top'
            },
            { // sinistra
                offsetX: 0,
                offsetY: 0,
                originX: 'start',
                originY: 'top',
                overlayX: 'end',
                overlayY: 'top'
            }
        ];
    }

    private createOverlay(origin) {
        const overlayConfig = this.getOverlayConfig(origin);
        return this.overlay.create(overlayConfig);
    }

    private attachDialogContainer(overlayRef: OverlayRef, dialogRef: SelectedItemsVisualizerOverlayRef,
        selectedItemsObservable: Observable<ItemNode[]>, itemList: ItemNode[]) {
        const injector = this.createInjector(dialogRef, selectedItemsObservable, itemList);
        const containerPortal = new ComponentPortal(SelectedItemsVisualizerOverlayComponent, null, injector);

        overlayRef.attach(containerPortal);
    }

    private createInjector(dialogRef: SelectedItemsVisualizerOverlayRef, selectedItemsObservable: Observable<ItemNode[]>, itemList: ItemNode[]): Injector {
        return Injector.create({
            parent: this.injector,
            providers: [
                { provide: DIALOG_REF_TOKEN, useValue: dialogRef },
                { provide: SELECTED_ITEMS_STREAM, useValue: selectedItemsObservable },
                { provide: ITEM_LIST, useValue: new ItemListInjectionToken(itemList) }
            ]
        });

    }

}
